import React, { Suspense, useCallback, useState } from 'react';
import './BotsActivityPage.scss';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ConfirmDialog } from 'primereact/confirmdialog';
import Loader from '../../shared/components/common/loader/Loader';
import Title from '../../shared/components/title/Title';
import { BOTS_ACTIVITY_PROJECT } from '../../shared/routes/path';
import { useDisconnectCloudflare } from '../../shared/api/hooks/bot-sources/useDisconnectCloudflare';
import { useRunBotsActivityAnalysis } from '../../shared/api/hooks/bots-activity/useRunBotsActivityAnalysis';
import {
  GET_BOTS_ACTIVITY_CONTENT,
  GET_BOTS_ACTIVITY_OVERVIEW,
  GET_BOTS_ACTIVITY_STATUS,
  GET_BOTS_ACTIVITY_TIMESERIES,
} from '../../shared/api/query/bots-activity/keys';
import { BOT_SOURCES } from '../../shared/constants/botSources';
import { useRangeFromUrl, RANGE_OPTIONS } from './hooks/useRangeFromUrl';
import { useBotsActivityData, useBotsActivityContentData } from './hooks/useBotsActivityData';
import { useBotsActivityFilters } from './hooks/useBotsActivityFilters';
import { useBotsActivityAggregations } from './hooks/useBotsActivityAggregations';
import { BotsActivityHero } from './components/sections/BotsActivityHero';
import { BotsActivityKpiTiles } from './components/sections/BotsActivityKpiTiles';
import { BotsActivityVisitsPanel } from './components/sections/BotsActivityVisitsPanel';
import { BotsActivityContentPanel } from './components/sections/BotsActivityContentPanel';
import { BotsActivitySourceMenu } from './components/sections/BotsActivitySourceMenu';
import IconFilterDropdown from './components/IconFilterDropdown';
import { DataQualityPopover } from './components/data-quality/DataQualityPopover';
import {
  BotSourceKind,
  DataQualityState,
} from './components/data-quality/dataQualityHelpers';

type GranularityOption = 'hour' | 'day' | 'week';

const getDefaultGranularity = (range: string): GranularityOption => {
  if (range === '1d') return 'hour';
  if (range === '365d') return 'week';
  return 'day';
};

const parseProjectId = (value?: string): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const BotsActivityPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { projectId: projectIdParam } = useParams<{ projectId: string }>();
  const projectId = parseProjectId(projectIdParam);

  const [activeRange, setActiveRange] = useRangeFromUrl();
  const [granularity, setGranularity] = useState<GranularityOption>(
    getDefaultGranularity(activeRange)
  );

  const handleRangeChange = useCallback(
    (value: (typeof RANGE_OPTIONS)[number]) => {
      setActiveRange(value);
      setGranularity(getDefaultGranularity(value));
    },
    [setActiveRange]
  );

  const {
    status,
    statusLoading,
    overview,
    overviewError,
    timeseries,
    timeseriesError,
    cloudflareStatus,
    fastlyStatus,
    logUploads,
    refetchOverview,
    refetchTimeseries,
  } = useBotsActivityData(projectId, activeRange, granularity);

  const {
    platformFilter,
    setPlatformFilter,
    botTypeFilter,
    setBotTypeFilter,
    botFilter,
    setBotFilter,
    platformOptions,
    botTypeOptions,
    botOptions,
    filteredOverview,
    filteredTimeseries,
    allowedBotNames,
    anyFilterActive,
  } = useBotsActivityFilters(overview, timeseries);

  // Content queries run after the filters hook — they take the bot-name filter
  // so folder/URL aggregates are computed backend-side for the current selection.
  const {
    contentFolder,
    contentFolderError,
    refetchContentFolder,
    contentUrl,
    contentUrlError,
    refetchContentUrl,
  } = useBotsActivityContentData(projectId, activeRange, allowedBotNames);

  const {
    featuredPlatforms,
    topUrls,
    topFolders,
    excludedFileVisits,
    totals,
    headlineParts,
    hasNoActivityData,
    byPlatform,
    byBot,
    byBotType,
    failureRate,
    previousFailureRate,
  } = useBotsActivityAggregations(
    filteredOverview,
    contentFolder,
    contentUrl,
    filteredTimeseries,
    status,
    anyFilterActive
  );

  // After disconnect the backend keeps the integration row (isActive=false) so `integrationId`
  // alone is not a reliable connection signal. Treat the integration as gone unless `isConnected`.
  const cloudflareIsConnected = !!cloudflareStatus.data?.isConnected;
  const cloudflareIntegrationId = cloudflareIsConnected
    ? cloudflareStatus.data?.integrationId ?? null
    : null;
  const { disconnectWithConfirm, isPending: disconnectPending } = useDisconnectCloudflare(
    projectId ?? 0,
    cloudflareStatus.data?.integrationId ?? null
  );

  const {
    runAnalysis,
    data: runAnalysisData,
    isPending: runAnalysisPending,
    isError: runAnalysisIsError,
  } = useRunBotsActivityAnalysis(projectId);

  const [csvDialogOpen, setLogDialogOpen] = useState(false);

  const handleDisconnectCloudflare = useCallback(() => {
    disconnectWithConfirm(() => {
      cloudflareStatus.refetch();
      if (projectId) navigate(BOTS_ACTIVITY_PROJECT(projectId));
    });
  }, [disconnectWithConfirm, cloudflareStatus, projectId, navigate]);

  const handleLogDialogClose = useCallback(() => setLogDialogOpen(false), []);
  const handleLogDialogSuccess = useCallback(() => {
    setLogDialogOpen(false);
    logUploads.refetch();
    // The useUploadLog mutation already invalidates these keys, but we re-invalidate
    // here defensively so the analysis dashboard always picks up the just-aggregated
    // events even if the dialog closes before the mutation's onSuccess fires.
    queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_OVERVIEW, projectId] });
    queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_TIMESERIES, projectId] });
    queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_CONTENT, projectId] });
    queryClient.invalidateQueries({ queryKey: [GET_BOTS_ACTIVITY_STATUS, projectId] });
  }, [logUploads, queryClient, projectId]);

  const hasContentError = overviewError || timeseriesError || contentFolderError || contentUrlError;
  const handleRetryAll = useCallback(() => {
    if (overviewError) refetchOverview();
    if (timeseriesError) refetchTimeseries();
    if (contentFolderError) refetchContentFolder();
    if (contentUrlError) refetchContentUrl();
  }, [
    overviewError, timeseriesError, contentFolderError, contentUrlError,
    refetchOverview, refetchTimeseries, refetchContentFolder, refetchContentUrl,
  ]);

  if (statusLoading || cloudflareStatus.isLoading || fastlyStatus.isLoading || logUploads.isLoading) return <Loader />;

  const fastlyIsConnected = !!fastlyStatus.data?.isConnected;
  const hasIntegration = cloudflareIsConnected || fastlyIsConnected || logUploads.data.length > 0;
  if (!hasIntegration && projectId) return <Navigate to={BOTS_ACTIVITY_PROJECT(projectId)} replace />;

  // Resolve the *single* active source for this view. Mutual exclusion is
  // enforced at connect time (one-source-per-project) so exactly one branch is
  // truthy here when the page renders.
  const activeSource: BotSourceKind = cloudflareIsConnected
    ? 'cloudflare'
    : fastlyIsConnected
      ? 'fastly'
      : logUploads.data.length > 0
        ? 'log'
        : null;
  const dataQualityState: DataQualityState = {
    source: activeSource,
    supportsBotManagement:
      activeSource === 'cloudflare'
        ? cloudflareStatus.data?.supportsBotManagement ?? null
        : activeSource === 'fastly'
          ? fastlyStatus.data?.supportsAiCrawlerCount ?? null
          : null,
  };
  const activeSourceConfig =
    activeSource === 'cloudflare'
      ? BOT_SOURCES.cloudflare
      : activeSource === 'fastly'
        ? BOT_SOURCES.fastly
        : null;

  const updatedAt = status?.lastDataAt ? new Date(status.lastDataAt) : null;
  const headlineFolder =
    !headlineParts?.folder || headlineParts.folder === '/'
      ? t('botsActivity.homepage')
      : headlineParts.folder;
  const headlineText = headlineParts
    ? t('botsActivity.headline', { platform: headlineParts.platform, sharePct: headlineParts.sharePct, folder: headlineFolder })
    : t('botsActivity.headlineFallback');

  const LogDialog = BOT_SOURCES.log?.dialogComponent ?? null;

  const showEmptyBanner = !!status && !status.hasData;
  // Cloudflare and Fastly both expose a manual poll via the same backend
  // endpoint (`/run-analysis` dispatches to whichever provider the project
  // uses). Log-only integrations are excluded — for them "Run analysis"
  // would just re-trigger the raw→hourly aggregation, which already runs
  // synchronously during upload, so the button would be a no-op for users.
  const canRunAnalysis = cloudflareIsConnected || fastlyIsConnected;

  // Backend tells us which provider serviced the manual poll (`data.source`)
  // for `status='ok'`. For auth_failed we fall back to the page-level
  // `activeSource` because at most one integration is active per project,
  // so that's an unambiguous mapping.
  const runAnalysisStatusMessage = ((): {
    text: string;
    modifier: 'success' | 'error' | 'warn';
  } | null => {
    if (runAnalysisPending) return null;
    if (runAnalysisData) {
      switch (runAnalysisData.status) {
        case 'ok': {
          if ((runAnalysisData.rowsIngested ?? 0) > 0) {
            return {
              text: t('botsActivity.runAnalysisOk', { rows: runAnalysisData.rowsIngested }),
              modifier: 'success',
            };
          }
          const sourceKey =
            runAnalysisData.source === 'fastly'
              ? 'botsActivity.runAnalysisOkEmptyFastly'
              : runAnalysisData.source === 'log_upload'
                ? 'botsActivity.runAnalysisOkEmptyLog'
                : 'botsActivity.runAnalysisOkEmptyCloudflare';
          return { text: t(sourceKey), modifier: 'success' };
        }
        case 'rate_limited':
        case 'cloudflare_rate_limited':
        case 'fastly_rate_limited':
          return {
            text: t('botsActivity.runAnalysisRateLimited', {
              seconds: runAnalysisData.retryAfterSeconds ?? 60,
            }),
            modifier: 'warn',
          };
        case 'auth_failed': {
          const authKey =
            activeSource === 'fastly'
              ? 'botsActivity.runAnalysisAuthFailedFastly'
              : 'botsActivity.runAnalysisAuthFailedCloudflare';
          return { text: t(authKey), modifier: 'error' };
        }
        case 'no_integration':
        case 'inactive':
          return { text: t('botsActivity.runAnalysisNoIntegration'), modifier: 'warn' };
        case 'failed':
        default:
          return {
            text: t('botsActivity.runAnalysisFailed', {
              error: runAnalysisData.error ?? '',
            }),
            modifier: 'error',
          };
      }
    }
    if (runAnalysisIsError) {
      return { text: t('botsActivity.runAnalysisFailed', { error: '' }), modifier: 'error' };
    }
    return null;
  })();

  return (
    <>
      <div className="bots-activity-page page-content">
        <div className="page-box">
          <div className="page-box__filters" data-observe-sticky>
            <Title
              isBaseCategory={false}
              title={
                <>
                  <span className="bots-activity__title-text">{t('botsActivity.title')}</span>
                  {activeSourceConfig && (
                    <span className="bots-activity__title-source">
                      {activeSourceConfig.logoPath && (
                        <img
                          src={activeSourceConfig.logoPath}
                          alt=""
                          className="bots-activity__source-logo"
                        />
                      )}
                      <span className="bots-activity__title-source-name">
                        {activeSourceConfig.displayName ?? ''}
                      </span>
                    </span>
                  )}
                  <DataQualityPopover state={dataQualityState} />
                </>
              }
            >
              <div className="filters bots-activity__filters">
                <IconFilterDropdown
                  iconClass="ico--calendar"
                  value={activeRange}
                  options={RANGE_OPTIONS.map((r) => ({
                    value: r,
                    label: t(`botsActivity.ranges.${r}`),
                  }))}
                  onChange={(v) => handleRangeChange(v as (typeof RANGE_OPTIONS)[number])}
                />
                <IconFilterDropdown
                  iconClass="ico--bot"
                  value={platformFilter}
                  options={platformOptions}
                  onChange={setPlatformFilter}
                />
                <IconFilterDropdown
                  iconClass="ico--stats"
                  value={botTypeFilter}
                  options={botTypeOptions}
                  onChange={setBotTypeFilter}
                />
                <IconFilterDropdown
                  iconClass="ico--crawl"
                  value={botFilter}
                  options={botOptions}
                  onChange={setBotFilter}
                />
                <BotsActivitySourceMenu
                  cloudflareIntegrationId={cloudflareIntegrationId}
                  hasAnyIntegration={cloudflareIsConnected || logUploads.data.length > 0}
                  disconnectPending={disconnectPending}
                  runAnalysisPending={runAnalysisPending}
                  onUploadCsv={() => setLogDialogOpen(true)}
                  onRunAnalysis={() => runAnalysis()}
                  onDisconnectCloudflare={handleDisconnectCloudflare}
                  onOpenConnect={() =>
                    projectId && navigate(`${BOTS_ACTIVITY_PROJECT(projectId)}?manage=1`)
                  }
                />
              </div>
            </Title>
          </div>

          <div className="page-box__stats">
            {showEmptyBanner && (
              <div className="bots-activity__empty-banner">
                <div className="bots-activity__empty-banner-text">
                  <div className="bots-activity__empty-banner-title">
                    {status?.backfillInProgress
                      ? t('botsActivity.backfillOnly')
                      : t('botsActivity.emptyBannerTitle')}
                  </div>
                  <div className="bots-activity__empty-banner-hint">
                    {canRunAnalysis
                      ? t('botsActivity.emptyBannerHint')
                      : t('botsActivity.emptyBannerHintLog')}
                  </div>
                </div>
                {canRunAnalysis && (
                  <button
                    type="button"
                    className="btn btn--primary bots-activity__run-analysis-btn"
                    onClick={() => runAnalysis()}
                    disabled={runAnalysisPending}
                  >
                    <span className="ico--refresh ico--xs" aria-hidden="true" />
                    {runAnalysisPending
                      ? t('botsActivity.runningAnalysis')
                      : t('botsActivity.runAnalysis')}
                  </button>
                )}
              </div>
            )}

            {runAnalysisStatusMessage && (
              <div
                className={`bots-activity__run-banner bots-activity__run-banner--${runAnalysisStatusMessage.modifier}`}
                role="status"
              >
                <span className="bots-activity__run-banner-icon" aria-hidden="true">
                  {runAnalysisStatusMessage.modifier === 'success'
                    ? '✓'
                    : runAnalysisStatusMessage.modifier === 'error'
                      ? '!'
                      : '•'}
                </span>
                <span className="bots-activity__run-banner-text">
                  {runAnalysisStatusMessage.text}
                </span>
              </div>
            )}

            {status?.backfillInProgress && status?.hasData && (
              <div className="warning-box bots-activity__banner">
                {t('botsActivity.backfillInProgress')}
              </div>
            )}
            {hasContentError && (
              <div className="warning-box warning-box--error bots-activity__banner bots-activity__banner--error">
                {t('errorLoadingDataRetry')}
                <button className="btn btn--sm btn--secondary" type="button" onClick={handleRetryAll}>
                  {t('retry')}
                </button>
              </div>
            )}

            <BotsActivityHero
              updatedAt={updatedAt}
              headlineText={headlineText}
              tiles={
                <BotsActivityKpiTiles
                  totalVisits={totals.totalVisits}
                  visitsTrend={totals.visitsTrend}
                  indexedUrls={topUrls.length}
                  indexedUrlsTrend={null}
                  failureRate={failureRate}
                  previousFailureRate={previousFailureRate}
                  activeRange={activeRange}
                />
              }
            />

            <BotsActivityVisitsPanel
              granularity={granularity}
              onGranularityChange={setGranularity}
              featuredPlatforms={featuredPlatforms}
              timeseries={filteredTimeseries}
              hasNoActivityData={hasNoActivityData}
              activeRange={activeRange}
            />

            <BotsActivityContentPanel
              topFolders={topFolders}
              topUrls={topUrls}
              excludedFileVisits={excludedFileVisits}
              byPlatform={byPlatform}
              byBot={byBot}
              byBotType={byBotType}
            />
          </div>
        </div>
      </div>

      {csvDialogOpen && projectId && LogDialog && (
        <Suspense fallback={<Loader />}>
          <LogDialog
            projectId={projectId}
            visible={true}
            onClose={handleLogDialogClose}
            onSuccess={handleLogDialogSuccess}
            recentUploads={logUploads.data}
          />
        </Suspense>
      )}

      <ConfirmDialog className="dialog-box" />
    </>
  );
};

export default BotsActivityPage;
